import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Crown, ExternalLink, AlertTriangle } from "lucide-react";
import { formatUsd } from "./helpers";

interface FounderOverageRow {
  userId: number;
  email: string | null;
  name: string | null;
  tier: string | null;
  allotment: number;
  billableRooms: number;
  overageRooms: number;
  cycle: string;
  perRoomMonthlyRate: number;
  monthlyEquivalentCharge: number;
  estimatedNextCharge: number;
  subscriptionId: string;
  stripeUrl: string | null;
  stripeStatus: string | null;
  currentPeriodEnd: string | null;
  stripeQuantity: number | null;
  paymentFailed: boolean;
}

interface FounderOveragesResponse {
  founders: FounderOverageRow[];
  stripeEnabled: boolean;
  totalFounders: number;
  totalOverageRooms: number;
  totalMonthlyEquivalent: number;
}

function tierLabel(tier: string | null): string {
  if (!tier) return "—";
  const map: Record<string, string> = {
    Founder25: "Founder 25",
    Founder50: "Founder 50",
    Founder100: "Founder 100",
  };
  return map[tier] || tier;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function FounderOveragesPanel() {
  const query = useQuery<FounderOveragesResponse>({
    queryKey: ["/api/admin/billing/founder-overages"],
    refetchInterval: 60_000,
  });

  const data = query.data;
  const rows = data?.founders ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="text-founder-overages-title">
            <Crown className="h-5 w-5 text-brand-accent" />
            Founder Overage Subscriptions
          </h3>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Every founder with an active overage subscription, the rooms above their free allotment,
            and the amount Stripe will bill at next renewal. Monthly founders are billed the
            monthly figure; annual founders are billed 12× up front.
          </p>
        </div>
      </div>

      {data && !data.stripeEnabled && (
        <Card className="p-4 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40" data-testid="card-founder-overages-stripe-disabled">
          <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            Stripe is not enabled in this environment — live subscription status and renewal dates are unavailable. Charge estimates are computed from local room counts.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4" data-testid="card-founder-overages-total-founders">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Founders Billing Overage</div>
          <div className="text-2xl font-semibold" data-testid="text-founder-overages-total-founders">
            {query.isLoading ? <Skeleton className="h-8 w-12" /> : data?.totalFounders ?? 0}
          </div>
        </Card>
        <Card className="p-4" data-testid="card-founder-overages-total-rooms">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Overage Rooms</div>
          <div className="text-2xl font-semibold" data-testid="text-founder-overages-total-rooms">
            {query.isLoading ? <Skeleton className="h-8 w-12" /> : data?.totalOverageRooms ?? 0}
          </div>
        </Card>
        <Card className="p-4" data-testid="card-founder-overages-total-monthly">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Monthly-Equivalent Revenue</div>
          <div className="text-2xl font-semibold" data-testid="text-founder-overages-total-monthly">
            {query.isLoading ? <Skeleton className="h-8 w-20" /> : formatUsd(data?.totalMonthlyEquivalent ?? 0)}
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden" data-testid="card-founder-overages-table">
        {query.isLoading ? (
          <div className="p-6"><Skeleton className="h-32 w-full" /></div>
        ) : query.isError ? (
          <div className="p-6 text-center text-sm text-red-600" data-testid="text-founder-overages-error">
            Failed to load founder overage subscriptions.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="text-founder-overages-empty">
            No founders currently have an active overage subscription.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right">Allotment</TableHead>
                  <TableHead className="text-right">Billable Rooms</TableHead>
                  <TableHead className="text-right">Overage Rooms</TableHead>
                  <TableHead>Cycle</TableHead>
                  <TableHead className="text-right">Next Charge</TableHead>
                  <TableHead>Renews</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.userId} data-testid={`row-founder-overage-${row.userId}`}>
                    <TableCell className="text-sm">
                      <div className="font-medium" data-testid={`text-founder-overage-name-${row.userId}`}>
                        {row.name || `User #${row.userId}`}
                      </div>
                      {row.email && (
                        <div className="text-xs text-muted-foreground" data-testid={`text-founder-overage-email-${row.userId}`}>
                          {row.email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`text-founder-overage-tier-${row.userId}`}>
                      {tierLabel(row.tier)}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono" data-testid={`text-founder-overage-allotment-${row.userId}`}>
                      {row.allotment}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono" data-testid={`text-founder-overage-billable-${row.userId}`}>
                      {row.billableRooms}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono" data-testid={`text-founder-overage-rooms-${row.userId}`}>
                      <span className={row.overageRooms > 0 ? "font-semibold" : "text-muted-foreground"}>
                        {row.overageRooms}
                      </span>
                      {row.stripeQuantity != null && row.stripeQuantity !== row.overageRooms && (
                        <Badge
                          variant="outline"
                          className="ml-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200"
                          data-testid={`badge-founder-overage-qty-mismatch-${row.userId}`}
                        >
                          Stripe: {row.stripeQuantity}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm capitalize" data-testid={`text-founder-overage-cycle-${row.userId}`}>
                      {row.cycle}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono" data-testid={`text-founder-overage-charge-${row.userId}`}>
                      {formatUsd(row.estimatedNextCharge)}
                      <div className="text-xs text-muted-foreground">
                        {formatUsd(row.monthlyEquivalentCharge)}/mo
                      </div>
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`text-founder-overage-renews-${row.userId}`}>
                      {formatDate(row.currentPeriodEnd)}
                    </TableCell>
                    <TableCell>
                      {row.paymentFailed ? (
                        <Badge className="bg-red-100 text-red-800 border-red-300" data-testid={`badge-founder-overage-payment-failed-${row.userId}`}>
                          Payment failed
                        </Badge>
                      ) : row.stripeStatus ? (
                        <Badge
                          variant={row.stripeStatus === "active" ? "default" : "outline"}
                          className="capitalize"
                          data-testid={`badge-founder-overage-status-${row.userId}`}
                        >
                          {row.stripeStatus.replace(/_/g, " ")}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid={`text-founder-overage-status-unknown-${row.userId}`}>
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.stripeUrl && (
                        <a
                          href={row.stripeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-brand hover:underline"
                          data-testid={`link-founder-overage-stripe-${row.userId}`}
                        >
                          Stripe <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
